import { useEffect, useState } from "react";
import AppConfig from "../config/config";
import VoiceApiService from "../services/apiServices/VoiceApiService";
import { isVoiceRecordingSupported } from "../services/helpers/VoiceRecorder";
import {
  VOICE_DEFAULT_LIMITS,
  VoiceCapabilities,
  VoiceLimits,
  VoiceCapabilitySkillId,
} from "../interfaces/VoiceObject";

export interface VoiceCapabilityState {
  /** True once the capabilities answer (or failure) is in. */
  ready: boolean;
  capabilities: VoiceCapabilities | null;
  /** Show the voice entry for this skill? (flag && server enabled && skill enabled && recorder supported) */
  isSkillAvailable: (skillId: VoiceCapabilitySkillId) => boolean;
  /** Server limits, falling back to VOICE_DEFAULT_LIMITS. */
  limits: VoiceLimits;
}

/** Limits from capabilities, falling back field by field to the defaults. */
export function voiceLimitsFrom(capabilities: VoiceCapabilities | null | undefined): VoiceLimits {
  return {
    minDurationMs: capabilities?.minDurationMs || VOICE_DEFAULT_LIMITS.minDurationMs,
    maxDurationMs: capabilities?.maxDurationMs || VOICE_DEFAULT_LIMITS.maxDurationMs,
    maxAudioBytes: capabilities?.maxAudioBytes || VOICE_DEFAULT_LIMITS.maxAudioBytes,
    acceptedMimeTypes:
      capabilities?.acceptedMimeTypes && capabilities.acceptedMimeTypes.length > 0
        ? capabilities.acceptedMimeTypes
        : VOICE_DEFAULT_LIMITS.acceptedMimeTypes,
  };
}

/**
 * Whether voice can be offered here. Fetches GET /api/v1/voice/capabilities
 * lazily (only when `active` and the build flag is on), cached for the app
 * session by VoiceApiService. A failed fetch means "hidden" — nothing breaks.
 *
 * @param active pass false where the entry could never show (e.g. a visitor's
 *               view of a business), so no request is made.
 */
export function useVoiceCapabilities(active: boolean, skillId: VoiceCapabilitySkillId = "product.edit"): VoiceCapabilityState {
  const flagOn = (skillId === "product.create" ? AppConfig.featureFlags.enableVoiceCreate : AppConfig.featureFlags.enableVoiceEdit) === true;
  const shouldFetch = active && flagOn && isVoiceRecordingSupported();
  const [capabilities, setCapabilities] = useState<VoiceCapabilities | null>(null);
  const [ready, setReady] = useState(!shouldFetch);
  const [generation, setGeneration] = useState(0);

  // Re-read after an invalidation (FEATURE_DISABLED / SKILL_DISABLED mid-session).
  useEffect(() => VoiceApiService.subscribeCapabilities(() => setGeneration((g) => g + 1)), []);

  useEffect(() => {
    if (!shouldFetch) {
      setCapabilities(null);
      setReady(true);
      return;
    }
    let cancelled = false;
    setReady(false);
    VoiceApiService.getCapabilities().then((caps) => {
      if (cancelled) return;
      setCapabilities(caps);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [shouldFetch, generation]);

  const isSkillAvailable = (skillId: VoiceCapabilitySkillId): boolean =>
    shouldFetch &&
    (skillId === 'product.create' ? AppConfig.featureFlags.enableVoiceCreate : AppConfig.featureFlags.enableVoiceEdit) &&
    !!capabilities?.enabled &&
    !!capabilities.skills?.some((skill) => skill.skillId === skillId && skill.enabled);

  return { ready, capabilities, isSkillAvailable, limits: voiceLimitsFrom(capabilities) };
}

export default useVoiceCapabilities;
